import React from 'react';
import { useSWRConfig } from 'swr';
import { Col, Row } from 'react-bootstrap';
import { useAuth } from 'react-oidc-context';
import { useParams } from 'react-router-dom';
import { Label } from '@progress/kendo-react-labels';
import { Button } from '@progress/kendo-react-buttons';
import { NumericTextBox } from '@progress/kendo-react-inputs';
import { DropDownList } from '@progress/kendo-react-dropdowns';

import {
  NotificationContent,
  NotificationContext
} from '../../../contexts/notification';
import { BankItem } from '../../../types/responses/bank-response';
import { useBankAction } from '../../../hooks/use-inventory-bank';
import { GameDocumentContext } from '../../../contexts/game-document';
import { TabButtonComponent } from '../../../components/tabs/tab-button';
import { GetResourceValue } from '../../../utils/game-document/resources';
import { GetItemTitle } from '../../../utils/game-document/assets';

export const ItemDetails: React.FC<{
  activeTab: number;
  items: BankItem[];
  selectedItemId: String;
  setActiveTab: React.Dispatch<React.SetStateAction<number>>;
}> = ({ activeTab, items, selectedItemId, setActiveTab }) => {
  const { gameCode } = useParams();
  const { mutate } = useSWRConfig();
  const auth = useAuth();

  const [gameDocument] = React.useContext(GameDocumentContext);
  const [notification, setNotification] = React.useContext(NotificationContext);

  const { isMutating, trigger } = useBankAction(gameCode ?? '');

  const selectedItem = React.useMemo(() => {
    return items.find((i) => i.itemId === selectedItemId);
  }, [items, selectedItemId]);

  const item = React.useMemo(() => {
    if (!gameDocument.gameDocument || !selectedItem) return undefined;
    const items = gameDocument.gameDocument.assets.items ?? [];

    const selected = items.find((i) => i.id === selectedItem.itemId);
    const image = GetResourceValue(
      gameDocument.gameDocument,
      selected?.imageResId ?? '',
      ''
    );

    return {
      id: selected?.id ?? '',
      image,
      name: GetItemTitle(gameDocument.gameDocument, selected?.id ?? ''),
      maxStock: selectedItem.maxStock,
      currentStock: selectedItem.currentStock
    };
  }, [selectedItem, gameDocument]);

  const onSubmit = React.useCallback(
    async (e: FormData) => {
      const action = e.get('action') as 'Decrease by' | 'Increase by';
      const amount = parseInt(e.get('amount') as string);

      const result = await trigger({
        action: action === 'Decrease by' ? 'acquire' : 'return',
        actor: auth.user?.profile.name ?? '',
        actorId: auth.user?.profile.sub ?? '',
        itemId: item?.id ?? '',
        quantity: amount
      });

      if (!result.success) {
        const responseTeamNotification: NotificationContent = {
          icon: 'notifications',
          isHide: false,
          message: (
            <span>
              <strong>{result.reason}</strong>
            </span>
          )
        };
        const content: NotificationContent[] = notification.content;
        content.push(responseTeamNotification);
        setNotification({ ...notification, content });
      }

      mutate(`games/${gameCode}/bank`);
    },
    [gameCode, item, auth.user]
  );

  if (!item) return;

  return (
    <div className={'col-10 d-flex flex-grow-1 flex-column gap-2 pr-0 pl-2'}>
      <Row className={'ml-3'}>
        <Row className={'container-tab-component'}>
          {['Details'].map((item, index) => (
            <TabButtonComponent
              key={item}
              thisTabIndex={index}
              activeTab={activeTab}
              title={item}
              onSetActiveTab={(tabIndex) => {
                setActiveTab(tabIndex);
              }}
            />
          ))}
        </Row>
        <hr />
        <Row>
          <Col className={'col-3'}>
            <div className={'d-flex'}>
              <div className={'d-flex justify-content-center w-full'}>
                <div
                  className={
                    'd-flex justify-content-between align-items-center'
                  }>
                  <img
                    width={150}
                    height={150}
                    src={item.image}
                    style={{
                      objectFit: 'cover',
                      borderRadius: '100%'
                    }}
                    alt={item.name}
                  />
                </div>
              </div>
            </div>
          </Col>
          <Col className={'d-flex flex-column justify-content-center'}>
            <h1 className={'text-primary fw-bold'}>{item.name}</h1>
            <span>
              Max stock: <b>{item.maxStock}</b>
            </span>
            <span>
              Available:{' '}
              <b>
                {item.currentStock === 0 ? (
                  <span className={'text-danger'}>Out of stock</span>
                ) : (
                  item.currentStock
                )}
              </b>
            </span>
          </Col>
        </Row>
        <hr className={'mt-2'} />
        <form
          className={'d-flex justify-content-between align-items-end'}
          action={onSubmit}>
          <div className={'d-flex gap-2'}>
            <div className={'d-flex flex-column'}>
              <Label className={'text-secondary'}>Master override</Label>
              <DropDownList
                className={'border border-dark'}
                name={'action'}
                defaultValue={'Decrease by'}
                style={{
                  width: '350px',
                  backgroundColor: 'white'
                }}
                data={['Decrease by', 'Increase by']}
              />
            </div>
            <div className={'d-flex flex-column'}>
              <Label className={'text-secondary'}>Amount</Label>
              <NumericTextBox
                defaultValue={0}
                name={'amount'}
                min={0}
                width={100}
              />
            </div>
          </div>

          <Button
            themeColor={'success'}
            className={'w-8 ml-2'}
            type={'submit'}
            disabled={isMutating}>
            Confirm
          </Button>
        </form>
      </Row>
    </div>
  );
};
